import * as React from 'react'
import * as MenuPrimitive from '@radix-ui/react-dropdown-menu'
import { cn } from '@/lib/utils'

/**
 * A dropdown menu, matching the design system's `.ds-menu`.
 *
 * A real menu rather than a popover full of buttons: arrow keys move between
 * items, typing jumps to one, Escape closes and returns focus to the trigger,
 * and the whole thing announces itself as a menu. Radix owns that behavior —
 * this file is only the design system's clothing for it.
 *
 * A menu is a **floating layer**, which is why it is one of the few places in
 * the app a shadow lives (`--ds-shadow-overlay`).
 */
export const Menu = MenuPrimitive.Root

/** The control that opens the menu. */
export const MenuTrigger = MenuPrimitive.Trigger

/** A hairline between groups of items — most often before the destructive one. */
export const MenuSeparator = React.forwardRef<
  React.ComponentRef<typeof MenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.Separator>
>(function MenuSeparator({ className, ...props }, ref) {
  return (
    <MenuPrimitive.Separator
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-line', className)}
      {...props}
    />
  )
})

/** The floating panel, portaled to the body. */
export const MenuContent = React.forwardRef<
  React.ComponentRef<typeof MenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.Content>
>(function MenuContent({ className, align = 'end', sideOffset = 4, ...props }, ref) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-50 min-w-52 rounded-md border border-line bg-surface p-1 text-ink shadow-md outline-none',
          'motion-safe:animate-[ds-tooltip-in_120ms_var(--ds-ease-out)]',
          className,
        )}
        {...props}
      />
    </MenuPrimitive.Portal>
  )
})

export interface MenuItemProps extends React.ComponentPropsWithoutRef<typeof MenuPrimitive.Item> {
  /** Danger ink. Reserved for the item that removes or ends something. */
  destructive?: boolean | undefined
}

/**
 * One item. Icon then label — an item with no icon still lines up with its
 * neighbors, because the icon slot is part of the row rather than a prefix.
 */
export const MenuItem = React.forwardRef<React.ComponentRef<typeof MenuPrimitive.Item>, MenuItemProps>(
  function MenuItem({ className, destructive = false, ...props }, ref) {
    return (
      <MenuPrimitive.Item
        ref={ref}
        className={cn(
          'flex min-h-8 cursor-pointer select-none items-start gap-2.5 rounded-sm px-2 py-1.5 text-sm outline-none',
          'data-[highlighted]:bg-surface-hover',
          // A disabled item says why in its own second line, so it stays legible.
          'data-[disabled]:pointer-events-none data-[disabled]:text-ink-muted',
          destructive ? 'text-danger-ink' : 'text-ink',
          className,
        )}
        {...props}
      />
    )
  },
)
